import { isEnvTruthy } from '@proj-airi/stage-shared'
import { ToasterPWAUpdateReady } from '@proj-airi/stage-ui/components'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { nanoid } from 'nanoid'
import { defineStore } from 'pinia'
import { computed, h, markRaw, onMounted, ref } from 'vue'
import { toast } from 'vue-sonner'

export const usePWAStore = defineStore('pwa', () => {
  const updateReadyHooks = ref<(() => void)[]>([])
  const breakpoints = useBreakpoints(breakpointsTailwind)
  const isMobile = breakpoints.smaller('md')

  // 安装提示（beforeinstallprompt）
  const deferredPrompt = ref<Event | null>(null)
  const dismissedInstall = ref(false)
  const isIOS = ref(false)

  const canInstall = computed(() => deferredPrompt.value !== null)
  const showInstallCard = computed(
    () => !dismissedInstall.value && (canInstall.value || isIOS.value),
  )

  function install() {
    const e = deferredPrompt.value
    if (!e)
      return
    const evt = e as any
    evt.prompt()
    evt.userChoice?.then(() => {
      deferredPrompt.value = null
    })
  }

  function dismissInstall() {
    dismissedInstall.value = true
    localStorage.setItem('yunli-pwa-install-dismissed', '1')
  }

  onMounted(() => {
    if (typeof window === 'undefined')
      return
    dismissedInstall.value = localStorage.getItem('yunli-pwa-install-dismissed') === '1'
    isIOS.value = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window)

    const onBeforeInstall = (e: any) => {
      e.preventDefault()
      deferredPrompt.value = e as Event
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall as EventListener)
    window.addEventListener('appinstalled', () => {
      deferredPrompt.value = null
      dismissedInstall.value = true
    })

    if (isEnvTruthy(import.meta.env.VITE_APP_TARGET_HUGGINGFACE_SPACE))
      return

    import('../modules/pwa').then(({ registerSW }) => {
      const updateSW = registerSW({
        onNeedRefresh: () => {
          const id = nanoid()
          toast.custom(markRaw(h(ToasterPWAUpdateReady, { id, onUpdate: () => updateSW() })), {
            id,
            duration: 30000,
            position: isMobile.value ? 'top-center' : 'bottom-right',
          })
        },
      })
      updateReadyHooks.value.push(updateSW)
    })
  })

  return { updateReadyHooks, isMobile, deferredPrompt, dismissedInstall, isIOS, canInstall, showInstallCard, install, dismissInstall }
})
